
import React, { useState } from 'react';
import { Header } from './components/Header';
import { DateSelector } from './components/DateSelector';
import { ReportDisplay } from './components/ReportDisplay';
import { generateNewsReport } from './services/geminiService';
import { ReportStatus, ReportData } from './types';
import { Loader2, AlertCircle } from 'lucide-react';

const App: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [status, setStatus] = useState<ReportStatus>(ReportStatus.IDLE);
  const [error, setError] = useState<string | null>(null);

  const handleGenerate = async () => {
    if (!selectedDate) return;

    setStatus(ReportStatus.LOADING);
    setError(null);
    setReportData(null);

    try {
      await generateNewsReport(selectedDate, (data) => {
        // Update state as data streams in
        setReportData(data);
        setStatus(ReportStatus.STREAMING);
      });
      setStatus(ReportStatus.SUCCESS);
    } catch (err: any) {
      console.error(err);
      setError(err.message || '生成报告失败，请重试。');
      setStatus(ReportStatus.ERROR);
    }
  };

  const isLoading = status === ReportStatus.LOADING;
  const isStreaming = status === ReportStatus.STREAMING;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Header />

      <main className="flex-grow container mx-auto px-4 py-8 max-w-4xl">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-end gap-4 justify-between">
            <div className="flex-grow">
              <h2 className="text-lg font-semibold text-slate-800 mb-2">
                生成每日摘要
              </h2>
              <p className="text-slate-500 text-sm mb-4">
                选择日期以抓取并总结全网最重要的AI新闻。
              </p>
              <DateSelector 
                date={selectedDate} 
                onChange={setSelectedDate} 
                disabled={isLoading || isStreaming}
              />
            </div>
            <button
              onClick={handleGenerate}
              disabled={isLoading || isStreaming}
              className={`
                h-12 px-6 rounded-lg font-medium text-white transition-all duration-200 flex items-center justify-center min-w-[160px]
                ${(isLoading || isStreaming)
                  ? 'bg-blue-400 cursor-not-allowed' 
                  : 'bg-blue-600 hover:bg-blue-700 shadow-md hover:shadow-lg active:scale-95'}
              `}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  准备中...
                </>
              ) : isStreaming ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  生成中...
                </>
              ) : (
                '生成报告'
              )}
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-semibold text-red-800">生成错误</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        )}

        {reportData && (
          <ReportDisplay 
            content={reportData.markdown} 
            sources={reportData.sources} 
            date={selectedDate} 
          />
        )}
        
        {status === ReportStatus.IDLE && !reportData && !error && (
          <div className="text-center py-20 opacity-50">
            <div className="w-16 h-16 bg-slate-200 rounded-full mx-auto mb-4 flex items-center justify-center">
              <span className="text-3xl">📰</span>
            </div>
            <p className="text-slate-600 font-medium">准备分析AI世界</p>
            <p className="text-slate-400 text-sm">请选择上方日期开始</p>
          </div>
        )}
      </main>

      <footer className="bg-white border-t border-slate-200 py-6 mt-auto">
        <div className="container mx-auto px-4 text-center text-slate-400 text-sm">
          <p>© {new Date().getFullYear()} AI新闻通俗解释. Powered by Google Gemini 2.5.</p>
        </div>
      </footer>
    </div>
  );
};

export default App;
